import type { TimestampLike } from '../firestore/timestamp';

export type SensorKind = 'ruuvitag';

export type SensorSource = 'ruuvi-station' | 'ruuvi-gateway' | 'raspi-ble' | 'custom';

export interface SensorConfig {
  /** Calibrated temperature = rawTemperatureC + calibrationOffsetC. */
  calibrationOffsetC: number;
  /** UI-side freshness threshold; readings older than this are shown as stale. */
  staleAfterSeconds: number;
  /** Min seconds between sampled history writes while the sensor is part of an active sauna session. */
  historyActiveSampleSeconds: number;
  /** Min seconds between sampled history writes during normal/idle operation. */
  historyIdleSampleSeconds: number;
}

export interface SensorCurrentState<TTimestamp = TimestampLike> {
  observedAt: TTimestamp;
  receivedAt: TTimestamp;
  source: SensorSource;
  rawTemperatureC: number | null;
  temperatureC: number | null;
  humidityPct: number | null;
  pressureHpa: number | null;
  batteryMv: number | null;
  rssi: number | null;
  /** Set whenever a history doc was written for this state; null until the first sampled write. */
  lastSampledAt: TTimestamp | null;
}

export interface SensorDoc<TTimestamp = TimestampLike> {
  name: string;
  kind: SensorKind;
  location: string;
  physicalIds: string[];
  enabled: boolean;
  config: SensorConfig;
  currentState: SensorCurrentState<TTimestamp> | null;
  createdAt: TTimestamp;
  updatedAt: TTimestamp;
}

export interface UnknownSensorDoc<TTimestamp = TimestampLike> {
  physicalId: string;
  source: SensorSource;
  firstSeenAt: TTimestamp;
  lastSeenAt: TTimestamp;
  seenCount: number;
  lastPayloadSummary: {
    temperatureC: number | null;
    humidityPct: number | null;
    rssi: number | null;
  };
}

export const SENSOR_NAME_MAX = 80;
export const SENSOR_LOCATION_MAX = 80;
export const CALIBRATION_MIN = -50;
export const CALIBRATION_MAX = 50;
export const STALE_AFTER_MIN = 30;
export const STALE_AFTER_MAX = 3600;
export const DEFAULT_STALE_AFTER_SECONDS = 180;
export const HISTORY_SAMPLE_MIN = 10;
export const HISTORY_SAMPLE_MAX = 86_400;
export const DEFAULT_HISTORY_ACTIVE_SAMPLE_SECONDS = 60;
export const DEFAULT_HISTORY_IDLE_SAMPLE_SECONDS = 300;

/**
 * Returns true when the time since the last sampled history write is at or
 * beyond `intervalSec`. `null` lastSampledAt always returns true (no prior
 * sample → write the first one).
 */
export function shouldSampleHistory(lastSampledAtMs: number | null, nowMs: number, intervalSec: number): boolean {
  if (lastSampledAtMs === null) return true;
  if (!Number.isFinite(lastSampledAtMs) || !Number.isFinite(nowMs) || !Number.isFinite(intervalSec)) return true;
  return nowMs - lastSampledAtMs >= intervalSec * 1000;
}

// ─── Callable contracts ──────────────────────────────────────────────────────

export interface CreateSensorFromUnknownPayload {
  homeId: string;
  physicalId: string;
  name: string;
  location?: string;
  kind?: SensorKind;
  calibrationOffsetC?: number;
  staleAfterSeconds?: number;
}

export interface UpdateSensorPayload {
  homeId: string;
  sensorId: string;
  name?: string;
  location?: string;
  calibrationOffsetC?: number;
  staleAfterSeconds?: number;
  historyActiveSampleSeconds?: number;
  historyIdleSampleSeconds?: number;
}

export interface SetSensorEnabledPayload {
  homeId: string;
  sensorId: string;
  enabled: boolean;
}
